/**
 * The Academy Lesson video spec (ADR 0009) and the pure MP4 container reader
 * that checks it. One implementation runs in the browser (instant feedback
 * before upload) and in the API (re-validation from a ranged read of the
 * stored object). No ffmpeg, no transcoding: files that break the spec are
 * rejected with a reason and a link to the export preset.
 *
 * Pure TypeScript with no Node or DOM imports.
 */

/** Where the export preset is published. Shown with every rejection. */
export const ACADEMY_VIDEO_EXPORT_PRESET_URL =
  "https://classtablet.com/academy/video-export-preset";

const BITS_PER_BYTE = 8;
const BYTES_PER_MIB = 1024 * 1024;

export const ACADEMY_VIDEO_SPEC = {
  container: "MP4",
  videoCodec: "H.264",
  /** H.264 `profile_idc` 100 is High. */
  profileIdc: 100,
  profileName: "High",
  audioCodec: "AAC",
  /** The vertical frame, used for a portrait lesson and in messages. */
  width: 1080,
  height: 1920,
  /**
   * The two frames a converted lesson may have (ADR 0014): vertical for a
   * portrait recording, horizontal for a landscape one.
   */
  frames: [
    { width: 1080, height: 1920 },
    { width: 1920, height: 1080 },
  ],
  /** Average bitrate of the whole file, audio included. */
  maxBitrateBps: 4_000_000,
  warnDurationSeconds: 120,
  maxDurationSeconds: 180,
} as const;

/**
 * Upload size cap. 180 s at 4 Mbps is 90 MB, so a spec-compliant file always
 * fits; anything bigger breaks the bitrate or duration rule anyway.
 */
export const ACADEMY_VIDEO_MAX_BYTES = 100 * BYTES_PER_MIB;

/**
 * What an Author may upload (ADR 0013). The server converts any of these into
 * one web-ready vertical MP4 that meets `ACADEMY_VIDEO_SPEC`, so the strict
 * spec describes the converted output, not the upload.
 */
export const ACADEMY_VIDEO_INPUT = {
  /** Phone recordings: MP4, QuickTime (iPhone) and WebM. */
  contentTypes: [
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "video/x-m4v",
  ],
  maxBytes: 500 * BYTES_PER_MIB,
  /** Longer videos are refused after upload with a plain reason. */
  maxDurationSeconds: 180,
  /** Multipart part size: the S3 minimum is 5 MiB, so 10 MiB is comfortable. */
  partBytes: 10 * BYTES_PER_MIB,
} as const;

/** Poster frames are small JPEG, PNG or WebP stills. */
export const ACADEMY_POSTER_MAX_BYTES = 2 * BYTES_PER_MIB;

export const ACADEMY_POSTER_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

/**
 *
 */
export type VideoViolationKey =
  | "container"
  | "videoCodec"
  | "profile"
  | "audioCodec"
  | "dimensions"
  | "faststart"
  | "bitrate"
  | "duration";

/**
 *
 */
export interface VideoViolation {
  key: VideoViolationKey;
  /** English text for logs and API clients; the dashboard translates by key. */
  message: string;
  /** Values the dashboard interpolates into its translated message. */
  params: Record<string, string | number>;
}

/**
 *
 */
export interface VideoWarning {
  key: "duration";
  message: string;
  params: Record<string, string | number>;
}

/** What the container says about the file. `null` means "not found". */
export interface Mp4Metadata {
  audio: { codec: string } | null;
  durationSeconds: number | null;
  /** True when the `moov` atom precedes `mdat` (progressive-download ready). */
  faststart: boolean | null;
  sizeBytes: number;
  video: {
    codec: string;
    height: number;
    profileIdc: number | null;
    width: number;
  } | null;
}

/**
 *
 */
export type Mp4ReadResult =
  | { ok: true; metadata: Mp4Metadata }
  | { ok: false; reason: string };

/** Reads `length` bytes at `start`. May return fewer at end of file. */
export type ByteRangeReader = (
  start: number,
  length: number
) => Promise<Uint8Array>;

// ── Container reader ────────────────────────────────────────────────────────

const BOX_HEADER_BYTES = 8;
const LARGE_BOX_HEADER_BYTES = 16;
const MAX_TOP_LEVEL_BOXES = 64;
const MAX_MOOV_BYTES = 16 * BYTES_PER_MIB;
const UINT32_RANGE = 2 ** 32;

/** Bytes between the sample entry header and its child boxes (visual). */
const VISUAL_ENTRY_FIXED_BYTES = 78;
const STSD_ENTRY_OFFSET = 8;

interface Box {
  end: number;
  start: number;
  type: string;
}

const readU32 = (view: DataView, at: number) => view.getUint32(at);

const readU64 = (view: DataView, at: number) =>
  view.getUint32(at) * UINT32_RANGE + view.getUint32(at + 4);

function fourcc(bytes: Uint8Array, at: number): string {
  return String.fromCharCode(
    bytes[at] ?? 0,
    bytes[at + 1] ?? 0,
    bytes[at + 2] ?? 0,
    bytes[at + 3] ?? 0
  );
}

function viewOf(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** Child boxes inside `bytes[start, end)`; stops at the first malformed box. */
function childBoxes(bytes: Uint8Array, start: number, end: number): Box[] {
  const view = viewOf(bytes);
  const boxes: Box[] = [];
  let at = start;

  while (at + BOX_HEADER_BYTES <= end) {
    let size = readU32(view, at);
    let header = BOX_HEADER_BYTES;

    if (size === 1 && at + LARGE_BOX_HEADER_BYTES <= end) {
      size = readU64(view, at + BOX_HEADER_BYTES);
      header = LARGE_BOX_HEADER_BYTES;
    } else if (size === 0) {
      size = end - at;
    }

    if (size < header || at + size > end) {
      break;
    }

    boxes.push({
      type: fourcc(bytes, at + 4),
      start: at + header,
      end: at + size,
    });
    at += size;
  }

  return boxes;
}

const find = (boxes: Box[], type: string) =>
  boxes.find((box) => box.type === type);

function parseMvhd(bytes: Uint8Array, box: Box): number | null {
  const view = viewOf(bytes);
  const version = bytes[box.start] ?? 0;
  const timescaleAt = box.start + (version === 1 ? 20 : 12);
  const durationAt = timescaleAt + 4;

  if (durationAt + (version === 1 ? 8 : 4) > box.end) {
    return null;
  }

  const timescale = readU32(view, timescaleAt);

  const duration =
    version === 1 ? readU64(view, durationAt) : readU32(view, durationAt);

  return timescale > 0 ? duration / timescale : null;
}

interface TrackInfo {
  audio: { codec: string } | null;
  video: Mp4Metadata["video"];
}

function parseSampleEntry(
  bytes: Uint8Array,
  handler: string,
  stsd: Box
): TrackInfo {
  const entryStart = stsd.start + STSD_ENTRY_OFFSET;

  if (entryStart + BOX_HEADER_BYTES > stsd.end) {
    return { video: null, audio: null };
  }

  const view = viewOf(bytes);
  const codec = fourcc(bytes, entryStart + 4);

  if (handler === "soun") {
    return { video: null, audio: { codec } };
  }

  const width = view.getUint16(entryStart + BOX_HEADER_BYTES + 24);
  const height = view.getUint16(entryStart + BOX_HEADER_BYTES + 26);
  const entryEnd = Math.min(stsd.end, entryStart + readU32(view, entryStart));

  const children = childBoxes(
    bytes,
    entryStart + BOX_HEADER_BYTES + VISUAL_ENTRY_FIXED_BYTES,
    entryEnd
  );

  const avcC = find(children, "avcC");

  const profileIdc =
    avcC && avcC.start + 2 <= avcC.end ? (bytes[avcC.start + 1] ?? null) : null;

  return { video: { codec, width, height, profileIdc }, audio: null };
}

function parseTrak(bytes: Uint8Array, trak: Box): TrackInfo {
  const mdia = find(childBoxes(bytes, trak.start, trak.end), "mdia");

  if (!mdia) {
    return { video: null, audio: null };
  }

  const mdiaChildren = childBoxes(bytes, mdia.start, mdia.end);
  const hdlr = find(mdiaChildren, "hdlr");
  const minf = find(mdiaChildren, "minf");

  if (!(hdlr && minf)) {
    return { video: null, audio: null };
  }

  const handler = fourcc(bytes, hdlr.start + 8);
  const stbl = find(childBoxes(bytes, minf.start, minf.end), "stbl");

  const stsd = stbl && find(childBoxes(bytes, stbl.start, stbl.end), "stsd");

  return stsd
    ? parseSampleEntry(bytes, handler, stsd)
    : { video: null, audio: null };
}

function parseMoov(bytes: Uint8Array) {
  const moovChildren = childBoxes(bytes, 0, bytes.byteLength);
  const mvhd = find(moovChildren, "mvhd");
  let video: Mp4Metadata["video"] = null;
  let audio: Mp4Metadata["audio"] = null;

  for (const trak of moovChildren.filter((box) => box.type === "trak")) {
    const info = parseTrak(bytes, trak);
    video = video ?? info.video;
    audio = audio ?? info.audio;
  }

  return {
    durationSeconds: mvhd ? parseMvhd(bytes, mvhd) : null,
    video,
    audio,
  };
}

/**
 * Reads the container metadata the spec cares about using only ranged reads:
 * box headers, then the `moov` atom. The `mdat` payload is never read, so a
 * faststart file costs two small reads and a late-`moov` file a few headers.
 */
export async function readMp4Metadata(
  read: ByteRangeReader,
  sizeBytes: number
): Promise<Mp4ReadResult> {
  let at = 0;
  let mdatBeforeMoov = false;

  for (let count = 0; at < sizeBytes && count < MAX_TOP_LEVEL_BOXES; count++) {
    const head = await read(
      at,
      Math.min(LARGE_BOX_HEADER_BYTES, sizeBytes - at)
    );

    if (head.byteLength < BOX_HEADER_BYTES) {
      break;
    }

    const view = viewOf(head);
    const type = fourcc(head, 4);

    if (count === 0 && type !== "ftyp") {
      return { ok: false, reason: "The file is not an MP4 (no ftyp box)." };
    }

    let size = readU32(view, 0);
    let header = BOX_HEADER_BYTES;

    if (size === 1 && head.byteLength >= LARGE_BOX_HEADER_BYTES) {
      size = readU64(view, BOX_HEADER_BYTES);
      header = LARGE_BOX_HEADER_BYTES;
    } else if (size === 0) {
      size = sizeBytes - at;
    }

    if (size < header || at + size > sizeBytes) {
      return { ok: false, reason: "The MP4 is truncated or damaged." };
    }

    if (type === "mdat") {
      mdatBeforeMoov = true;
    }

    if (type === "moov") {
      if (size > MAX_MOOV_BYTES) {
        return { ok: false, reason: "The MP4 metadata is unreasonably large." };
      }

      const payload = await read(at + header, size - header);

      if (payload.byteLength < size - header) {
        return { ok: false, reason: "The MP4 is truncated or damaged." };
      }

      return {
        ok: true,
        metadata: {
          sizeBytes,
          faststart: !mdatBeforeMoov,
          ...parseMoov(payload),
        },
      };
    }

    at += size;
  }

  return {
    ok: false,
    reason: "The MP4 has no moov box, so it cannot be played.",
  };
}

/** Structural slice of `Blob` so this file needs no DOM lib. */
interface BlobLike {
  readonly size: number;
  slice(start: number, end: number): { arrayBuffer(): Promise<ArrayBuffer> };
}

/** Browser entry point: parse a chosen `File` without reading all of it. */
export async function readMp4MetadataFromBlob(
  blob: BlobLike
): Promise<Mp4ReadResult> {
  return await readMp4Metadata(
    async (start, length) =>
      new Uint8Array(await blob.slice(start, start + length).arrayBuffer()),
    blob.size
  );
}

// ── Spec check ──────────────────────────────────────────────────────────────

const VIDEO_CODEC_LABELS: Record<string, string> = {
  avc1: "H.264",
  avc3: "H.264",
  hvc1: "HEVC (H.265)",
  hev1: "HEVC (H.265)",
  av01: "AV1",
  vp09: "VP9",
};

const H264_PROFILES: Record<number, string> = {
  66: "Baseline",
  77: "Main",
  88: "Extended",
  100: "High",
  110: "High 10",
  122: "High 4:2:2",
  244: "High 4:4:4",
};

const AUDIO_CODEC_LABELS: Record<string, string> = {
  mp4a: "AAC",
  "ac-3": "AC-3",
  "ec-3": "E-AC-3",
  opus: "Opus",
  alac: "ALAC",
};

const isH264 = (codec: string) => codec === "avc1" || codec === "avc3";

const mbps = (bps: number) => Math.round((bps / 1_000_000) * 100) / 100;

/**
 *
 */
export interface SpecCheck {
  violations: VideoViolation[];
  warnings: VideoWarning[];
}

/** Average bitrate of the whole file in bits per second, or null. */
export function averageBitrateBps(metadata: Mp4Metadata): number | null {
  const seconds = metadata.durationSeconds;

  if (!seconds || seconds <= 0) {
    return null;
  }

  return Math.round((metadata.sizeBytes * BITS_PER_BYTE) / seconds);
}

/** Every way the file breaks the spec, each with a fix, plus soft warnings. */
export function checkVideoSpec(metadata: Mp4Metadata): SpecCheck {
  const violations: VideoViolation[] = [];
  const warnings: VideoWarning[] = [];
  const spec = ACADEMY_VIDEO_SPEC;

  const { video, audio } = metadata;

  if (video) {
    const label = VIDEO_CODEC_LABELS[video.codec] ?? video.codec;

    if (isH264(video.codec)) {
      if (video.profileIdc !== spec.profileIdc) {
        const found =
          video.profileIdc === null
            ? "unknown"
            : (H264_PROFILES[video.profileIdc] ??
              `profile ${video.profileIdc}`);

        violations.push({
          key: "profile",
          message: `The H.264 profile is ${found}. Export with the ${spec.profileName} profile.`,
          params: { found },
        });
      }
    } else {
      violations.push({
        key: "videoCodec",
        message: `The video is ${label}. Export it as ${spec.videoCodec}.`,
        params: { found: label },
      });
    }

    const fits = spec.frames.some(
      (frame) => frame.width === video.width && frame.height === video.height
    );

    if (!fits) {
      violations.push({
        key: "dimensions",
        message: `The video is ${video.width}x${video.height}. Export it vertical at ${spec.width}x${spec.height} or horizontal at ${spec.height}x${spec.width}.`,
        params: { width: video.width, height: video.height },
      });
    }
  } else {
    violations.push({
      key: "videoCodec",
      message: `The file has no video track. Export it as ${spec.videoCodec}.`,
      params: { found: "none" },
    });
  }

  if (audio) {
    if (audio.codec !== "mp4a") {
      const found = AUDIO_CODEC_LABELS[audio.codec] ?? audio.codec;
      violations.push({
        key: "audioCodec",
        message: `The audio is ${found}. Export it as ${spec.audioCodec}.`,
        params: { found },
      });
    }
  } else {
    violations.push({
      key: "audioCodec",
      message: `The file has no audio track. Export it with ${spec.audioCodec} audio.`,
      params: { found: "none" },
    });
  }

  if (metadata.faststart === false) {
    violations.push({
      key: "faststart",
      message:
        "The metadata (moov) is at the end of the file. Export with Fast Start enabled so playback begins immediately.",
      params: {},
    });
  }

  const seconds = metadata.durationSeconds;

  if (seconds === null || seconds <= 0) {
    violations.push({
      key: "duration",
      message: "The duration could not be read from the file.",
      params: { seconds: 0, max: spec.maxDurationSeconds },
    });
  } else if (seconds > spec.maxDurationSeconds) {
    violations.push({
      key: "duration",
      message: `The Lesson is ${Math.round(seconds)} seconds. The limit is ${spec.maxDurationSeconds} seconds; split it into shorter Lessons.`,
      params: { seconds: Math.round(seconds), max: spec.maxDurationSeconds },
    });
  } else if (seconds > spec.warnDurationSeconds) {
    warnings.push({
      key: "duration",
      message: `The Lesson is ${Math.round(seconds)} seconds. Lessons over ${spec.warnDurationSeconds} seconds are discouraged; consider splitting it.`,
      params: { seconds: Math.round(seconds), warn: spec.warnDurationSeconds },
    });
  }

  const bitrate = averageBitrateBps(metadata);

  if (bitrate !== null && bitrate > spec.maxBitrateBps) {
    violations.push({
      key: "bitrate",
      message: `The average bitrate is ${mbps(bitrate)} Mbps. The limit is ${mbps(spec.maxBitrateBps)} Mbps; lower the video bitrate.`,
      params: { found: mbps(bitrate), max: mbps(spec.maxBitrateBps) },
    });
  }

  return { violations, warnings };
}
